import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { useLanguage } from "@/i18n";
import { useMarket } from "@/market";
import { readConsent, saveConsent } from "@/lib/consent";

const TXT = {
  fr: {
    title: "Cookies et stockage",
    body: "Devisto n'utilise aucun cookie publicitaire ni de suivi tiers. Les stockages strictement nécessaires (session, langue, pays) sont toujours actifs. Vous pouvez accepter ou refuser le stockage de confort : mémorisation de votre appareil de confiance pendant 30 jours (évite un code de connexion à chaque fois).",
    accept: "Tout accepter", refuse: "Essentiels uniquement", more: "Politique de confidentialité",
    authority: { BE: "Autorité de protection des données (Belgique)", FR: "CNIL (France)", ES: "AEPD (Espagne)", LU: "CNPD (Luxembourg)" },
  },
  nl: {
    title: "Cookies en opslag",
    body: "Devisto gebruikt geen advertentie- of trackingcookies van derden. Strikt noodzakelijke opslag (sessie, taal, land) is altijd actief. U kunt de comfortopslag aanvaarden of weigeren: uw vertrouwd toestel 30 dagen onthouden (geen aanmeldcode bij elke login).",
    accept: "Alles aanvaarden", refuse: "Enkel essentieel", more: "Privacybeleid",
    authority: { BE: "Gegevensbeschermingsautoriteit (België)", FR: "CNIL (Frankrijk)", ES: "AEPD (Spanje)", LU: "CNPD (Luxemburg)" },
  },
  es: {
    title: "Cookies y almacenamiento",
    body: "Devisto no utiliza cookies publicitarias ni de seguimiento de terceros. El almacenamiento estrictamente necesario (sesión, idioma, país) está siempre activo. Puede aceptar o rechazar el almacenamiento de confort: recordar su dispositivo de confianza durante 30 días (evita un código de acceso en cada inicio de sesión).",
    accept: "Aceptar todo", refuse: "Solo esenciales", more: "Política de privacidad",
    authority: { BE: "APD (Bélgica)", FR: "CNIL (Francia)", ES: "AEPD (España)", LU: "CNPD (Luxemburgo)" },
  },
};

export const CookieBanner = () => {
  const { lang } = useLanguage();
  const { country } = useMarket();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    setOpen(!readConsent());
    const reopen = () => setOpen(true);
    window.addEventListener("devisto:consent-open", reopen);
    return () => window.removeEventListener("devisto:consent-open", reopen);
  }, []);
  if (!open) return null;
  const t = TXT[lang] || TXT.fr;
  const choose = (comfort) => { saveConsent({ comfort, country, lang }); setOpen(false); };

  return (
    <div data-testid="cookie-banner" role="dialog" aria-label={t.title} className="fixed inset-x-0 bottom-0 z-[80] px-4 pb-4 sm:px-6">
      <div className="mx-auto max-w-3xl rounded-2xl border border-[#E2E7F7] bg-white/95 backdrop-blur-md shadow-[0_12px_40px_-12px_rgba(15,38,69,0.35)] p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#EEF2FF] text-[#3A5CC8]"><Cookie className="h-4 w-4" /></span>
          <div className="min-w-0">
            <p className="font-heading text-base font-semibold text-[#0F2645]">{t.title}</p>
            <p className="mt-1 text-sm leading-relaxed text-[#5C6A87]">{t.body}</p>
            <p className="mt-2 text-xs text-[#93A0BC]">{t.authority[country] || t.authority.BE} · <Link to="/confidentialite" data-testid="cookie-more" className="underline underline-offset-2 hover:text-[#3A5CC8]">{t.more}</Link></p>
          </div>
        </div>
        <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button type="button" data-testid="cookie-refuse" onClick={() => choose(false)} className="rounded-full border border-[#E2E7F7] px-5 py-2.5 text-sm font-semibold text-[#0F2645] hover:bg-[#F6F8FD] transition-colors">{t.refuse}</button>
          <button type="button" data-testid="cookie-accept" onClick={() => choose(true)} className="rounded-full bg-[#3A5CC8] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#2F4CA8] transition-colors">{t.accept}</button>
        </div>
      </div>
    </div>
  );
};
